import { useState, useEffect, useCallback, useRef } from "react"
import { checkAuth, getStatus, isLoggedIn, setOnSessionExpired, logout } from "@/lib/api"
import PulseLogin from "@/components/PulseLogin"
import StatusBar from "@/components/StatusBar"
import Chat from "@/components/Chat"
import CommandScreen from "@/components/CommandScreen"
import ChatDrawer from "@/components/ChatDrawer"
import PulseLogo from "@/components/PulseLogo"
import GuidedTour, { shouldShowTour } from "@/components/GuidedTour"
import { Download, WifiOff } from "lucide-react"
import { usePWA } from "@/hooks/usePWA"

/** Maintenance overlay — shown when backend is unreachable */
function MaintenanceOverlay({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a0a14]/95 backdrop-blur-sm safe-top safe-bottom">
      <div className="text-center space-y-4 px-6 max-w-sm">
        <svg viewBox="0 0 120 80" className="w-28 h-20 mx-auto opacity-60">
          <ellipse cx="60" cy="50" rx="50" ry="20" fill="#1e1e3a" />
          <path d="M30 38 Q35 10, 60 15 Q85 10, 90 38 Q95 50, 80 55 Q60 65, 40 55 Q25 50, 30 38Z" fill="#2a2a4a" stroke="#4a4a6a" strokeWidth="1" />
          <circle cx="48" cy="32" r="3" fill="#6366f1" />
          <circle cx="72" cy="32" r="3" fill="#6366f1" />
          <path d="M52 42 Q60 48, 68 42" stroke="#6366f1" strokeWidth="1.5" fill="none" strokeLinecap="round" />
          <path d="M85 30 Q100 20, 105 35" stroke="#4a4a6a" strokeWidth="1" fill="none" />
        </svg>
        <h2 className="text-xl font-semibold text-white">We'll Be Right Back</h2>
        <p className="text-sm text-gray-400 leading-relaxed">
          CloakAI is restarting or experiencing a brief interruption. This usually takes less than a minute.
        </p>
        <div className="flex items-center justify-center gap-2 text-gray-500 text-xs">
          <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
          Reconnecting automatically...
        </div>
        <button onClick={onRetry} className="text-xs text-accent hover:underline mt-2">
          Try now
        </button>
      </div>
    </div>
  )
}

export type Tab = "status" | "chat" | "ops"

interface StatusData {
  status: string
  issues: string[]
  telemetry: Record<string, any>
  alerts: Array<{ severity: string; message: string }>
}

export default function App() {
  const [authed, setAuthed] = useState(false)
  const [email, setEmail] = useState("")
  const [checking, setChecking] = useState(true)
  const [forbidden, setForbidden] = useState(false)
  const [status, setStatus] = useState<StatusData | null>(null)
  const [apiDown, setApiDown] = useState(false)
  const apiFailCount = useRef(0)

  // PWA hook — must be called before any early returns (Rules of Hooks)
  const { canInstall, isOffline, install } = usePWA()

  const [showTour, setShowTour] = useState(false)

  useEffect(() => {
    setOnSessionExpired(() => {
      setAuthed(false)
      setEmail("")
    })

    if (!isLoggedIn()) {
      setChecking(false)
      return
    }
    checkAuth()
      .then(res => {
        if (res.ok) {
          setAuthed(true)
          setEmail(res.email || "")
          if (shouldShowTour()) setShowTour(true)
        } else if (res.forbidden) {
          setForbidden(true)
        }
        setChecking(false)
      })
      .catch(() => setChecking(false))
  }, [])

  const refreshStatus = async () => {
    try {
      const data = await getStatus()
      setStatus(data)
      apiFailCount.current = 0
      if (apiDown) setApiDown(false)
    } catch {
      apiFailCount.current++
      if (apiFailCount.current >= 3) setApiDown(true)
    }
  }

  useEffect(() => {
    if (!authed) return
    refreshStatus()
    const interval = apiDown ? 5000 : 30000
    const id = setInterval(refreshStatus, interval)
    return () => clearInterval(id)
  }, [authed, apiDown])

  const handleLogout = useCallback(async () => {
    await logout()
    setAuthed(false)
    setEmail("")
  }, [])

  if (checking) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-3">
          <PulseLogo size={48} className="animate-pulse" />
          <span className="text-gray-500 text-sm">Connecting to Pulse...</span>
        </div>
      </div>
    )
  }

  if (forbidden) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4 px-6 text-center">
        <PulseLogo size={56} />
        <h1 className="text-2xl font-semibold text-white">Access Denied</h1>
        <p className="text-gray-400 text-sm max-w-xs">
          Only the CloakAI team has access to Pulse.
        </p>
        <a href="/" className="text-accent hover:underline text-sm">
          Back to CloakAI
        </a>
        <span className="text-gray-700 text-xs mt-4 font-mono">ERR_PULSE_403</span>
      </div>
    )
  }

  if (!authed) {
    return (
      <PulseLogin onSuccess={async (userEmail) => {
        // Verify PULSE_EMAILS gate before granting access
        try {
          const res = await checkAuth()
          if (res.forbidden) {
            await logout()
            setForbidden(true)
            return
          }
          if (!res.ok) {
            await logout()
            return
          }
        } catch {
          // Fail closed: if access cannot be verified, do not grant UI access.
          await logout()
          setForbidden(true)
          return
        }
        setAuthed(true)
        setEmail(userEmail)
        if (shouldShowTour()) setShowTour(true)
      }} />
    )
  }

  return (
    <div className="flex flex-col h-screen">
      {/* Connection lost overlay */}
      {apiDown && <MaintenanceOverlay onRetry={refreshStatus} />}

      {/* PWA install banner */}
      {canInstall && (
        <div className="flex items-center justify-between px-3 py-1.5 bg-indigo-600/90 text-white text-xs">
          <span>Install Pulse for quick access from your home screen</span>
          <button onClick={install} className="flex items-center gap-1 px-2 py-0.5 rounded bg-white/20 hover:bg-white/30 font-medium">
            <Download size={11} /> Install
          </button>
        </div>
      )}
      {/* Offline indicator */}
      {isOffline && (
        <div className="flex items-center justify-center gap-1.5 px-3 py-1 bg-amber-600/90 text-white text-xs">
          <WifiOff size={11} /> Offline — showing cached data
        </div>
      )}
      <StatusBar status={status} email={email} onClickIssues={() => {}} onLogout={handleLogout} />

      {/* Desktop: 2-column — Chat (primary) | CommandScreen (sidebar) */}
      <div className="hidden md:flex flex-1 overflow-hidden">
        {/* Left: Chat */}
        <div className="flex-1 min-w-0">
          <Chat
            onActivity={() => { refreshStatus() }}
          />
        </div>

        {/* Divider */}
        <div className="w-px bg-border shrink-0" />

        {/* Right: CommandScreen (single-screen view) */}
        <div className="flex flex-col overflow-hidden shrink-0" style={{ width: 400 }}>
          <CommandScreen />
        </div>
      </div>

      {/* Mobile: CommandScreen full page + ChatDrawer overlay */}
      <div className="flex flex-col flex-1 overflow-hidden md:hidden">
        <CommandScreen />
        <ChatDrawer onActivity={() => { refreshStatus() }} />
      </div>

      {/* Guided tour overlay */}
      {showTour && (
        <GuidedTour
          onComplete={() => setShowTour(false)}
          onSwitchTab={() => {}}
        />
      )}
    </div>
  )
}
